import React, { useState, useRef, useEffect, useCallback } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import "./customdd.css";

const SimpleDropdown = ({
  selectValues,
  value,
  handleOnChange,
  dropdownLabel,
  disabled,
  clsNames,
  enableAutocomplete = true, // Default is true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [animationState, setAnimationState] = useState("closed");
  const [selectedValue, setSelectedValue] = useState(
    disabled ? undefined : value
  );
  const [searchTerm, setSearchTerm] = useState(""); // For autocomplete
  const [isTyping, setIsTyping] = useState(false); // Track if user is actively typing
  const [highlightedIndex, setHighlightedIndex] = useState(-1); // Track highlighted option for keyboard navigation

  const dropdownRef = useRef(null);
  const headerInputRef = useRef(null); // Ref for the header input (for autocomplete)

  // Update selected value when external state changes
  useEffect(() => {
    setSelectedValue(disabled ? undefined : value);
  }, [disabled, value]);

  // Define handleClose as a memoized callback to avoid dependency issues
  const handleClose = useCallback(() => {
    if (isOpen) {
      setAnimationState("close");
      setSearchTerm(""); // Clear search when closing
      setIsTyping(false); // Reset typing state when closing
      setHighlightedIndex(-1); // Reset highlighted index when closing
      setTimeout(() => {
        setIsOpen(false);
        setAnimationState("closed");
      }, 250); // Match animation duration
    }
  }, [isOpen]);

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [handleClose]);

  const handleOptionClick = (value) => {
    setSelectedValue(value);
    handleOnChange(value);
    setIsTyping(false); // Reset typing state when option is selected
    setSearchTerm(""); // Clear search term after selection
    handleClose();
  };

  const toggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        setIsOpen(true);
        setAnimationState("open");
        setHighlightedIndex(-1); // Reset highlighted index when opening
        // Focus header input when opening (if autocomplete is enabled)
        if (enableAutocomplete) {
          setTimeout(() => {
            headerInputRef.current?.focus();
          }, 100);
        }
      } else {
        handleClose();
      }
    }
  };

  // Find the label of the selected value
  const getSelectedLabel = () => {
    if (!selectedValue && dropdownLabel) return dropdownLabel;

    const selected = selectValues.find(
      (option) => option.value === selectedValue
    );
    return selected ? selected.label : dropdownLabel || "Select...";
  };

  // Filter options based on search term (for autocomplete)
  const filteredOptions =
    enableAutocomplete && searchTerm
      ? selectValues.filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : selectValues;

  // Get display value for autocomplete input
  const getDisplayValue = () => {
    // If user is actively typing, always show the search term (even if empty)
    if (isTyping) return searchTerm;
    // If user is not typing and has a selected value, show the selected label
    if (selectedValue) {
      const selected = selectValues.find(
        (option) => option.value === selectedValue
      );
      return selected ? selected.label : "";
    }
    // Otherwise show empty string
    return "";
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionClick(filteredOptions[highlightedIndex].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        handleClose();
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={dropdownRef}
      className={clsNames || ""}
      data-dropdown
      data-disabled={disabled ? "true" : "false"}
      data-state={isOpen ? "open" : "closed"}
    >
      {" "}
      <div
        data-part="header"
        onClick={toggleDropdown}
        onKeyDown={!enableAutocomplete ? handleKeyDown : undefined}
        tabIndex={!enableAutocomplete && !disabled ? 0 : -1}
      >
        {enableAutocomplete ? (
          <input
            ref={headerInputRef}
            type="text"
            placeholder={dropdownLabel || "Select..."}
            value={getDisplayValue()}
            autoComplete="off"
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setIsTyping(true); // User is actively typing
              setHighlightedIndex(-1); // Reset highlight when typing
              if (!isOpen) {
                setIsOpen(true);
                setAnimationState("open");
              }
            }}
            onFocus={() => {
              if (!isOpen) {
                setIsOpen(true);
                setAnimationState("open");
              }
              // Only start typing mode if user actually starts typing, not just on focus
            }}
            onKeyDown={handleKeyDown} // Add keyboard navigation
            onBlur={() => {
              // Small delay to check if click was on an option
              setTimeout(() => {
                if (!isOpen) {
                  setIsTyping(false);
                }
              }, 150);
            }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent header click from firing
              if (!isOpen) {
                setIsOpen(true);
                setAnimationState("open");
              }
            }}
            disabled={disabled}
            data-part="header-input"
          />
        ) : (
          <>
            {selectedValue ? (
              <div data-part="text">{getSelectedLabel()}</div>
            ) : (
              <div data-part="placeholder-text">{dropdownLabel}</div>
            )}
          </>
        )}
        <span data-part="icon" onClick={(e) => e?.stopPropagation()}>
          {isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </span>
      </div>
      {/* Keep the options in DOM during closing animation */}
      {(isOpen || animationState === "close") && !disabled && (
        <div data-part="options" data-animate={animationState}>
          {filteredOptions.length > 0
            ? filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  data-part="option"
                  data-selected={
                    selectedValue === option.value ? "true" : "false"
                  }
                  data-highlighted={
                    highlightedIndex === index ? "true" : "false"
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOptionClick(option.value);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)} // Update highlight on hover
                >
                  {option.label}
                </div>
              ))
            : enableAutocomplete &&
              searchTerm && <div data-part="no-results">No options found</div>}
        </div>
      )}
    </div>
  );
};

export default React.memo(SimpleDropdown);
